import { Action, Reducer } from 'redux';
import {
    GroupedResponseInventories,
    MetadataFieldTypes,
    Questionnaire,
    QuestionnairePageType,
    QuestionnaireQuestion, QuestionRegulatoryData,
    QuestionType,
    ScipSubmissionData,
    SurveyResponseInventoryData,
    SurveyResponseModel
} from '../../../Models/Surveys/SurveyDataModel';
import { SurveyResponseProgressTextHelper } from '../../../../components/Survey/SurveyResponse/SurveyResponseProgressTextHelper';
import { ResultType } from '../../../Models/Api/ApiResponse';
import { SimpleInventory } from '../../../Models/Inventories/SimpleInventory';
import { SurveyPageType } from '../../../Models/Surveys/SurveyDataModel';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../../';
import { DataResult, ToxnotServiceClient } from './../../../ToxnotServiceClient';
import { ProgressState, SurveyResponseState, NavContext, InventoryQuestionnaireType } from './SurveyResponseStates';
import { FileTableEntry } from '../../../Models/Documents/DocumentModels';
import { store } from '../../../..';
import {
    BomStatusItem,
    BomValidationStatus,
    CoreSurveyResponseData,
    ExtensionRequestModel,
    PageQuestionnaireStatuses,
    SurveyResponseValidationResult,
    RegulationScoresTable,
    InventoryDetailsResponse,
    PaginationDetails
} from './SurveyResponseModels';
import { nullOrEmptyCoalesce } from '../../../../utilities/Utilities';
import {ScipDataManager} from "../../../Managers/SurveyResponse/ScipDataManager";
import {RegulationManager} from "../../../Managers/RegulationManager";
import {CustomRegulationLogicManagerFactory} from "../../../Managers/CustomLogicManagers/CustomLogicManager";
import { FieldType } from '../../../../config/Models';

type KnownAction = SurveyResponseSetProgressState | SurveyResponseSetState;

// ===============================================================================

export const surveyResponseMaterialsPerPage = 25;

const LocalStorageNavContextKey = "survey-response-workflow-nav-context";

interface ArticleIdentifier {
    identifierTypeId: string;
    value: string;
    typeOtherText: string;
}

const DefaultScipArticleIdentifierId = "66306"; // part name

const SurveyResponseSetProgressState = "SET_RESPONSE_PROGRESS_STATE";
interface SurveyResponseSetProgressState {
    type: typeof SurveyResponseSetProgressState;
    value: Partial<ProgressState>;
}

const SurveyResponseSetState = "SET_RESPONSE_STATE";
interface SurveyResponseSetState {
    type: typeof SurveyResponseSetState;
    value: Partial<SurveyResponseState>;
}

const validationStatusMap: any = {
    "0" : "Fail",
    "1" : "Pass",
    "2" : "NoLongerAvailable",
    "3" : "NeverSold"
}

function getInitialNavContext(): NavContext {
    return {
        mainNavIndex: SurveyPageType.Instructions,
        currentPageOfMaterialsIndex: 0,
        inventoryNavIndex: -1,
        surveyQuestionnaireIndex: -1,
        surveyQuestionnairePageIndex: -1,
        previousPageStack: [],
        currentlySelectedScipSubmissionData: null,
        selectedInventoryPageIndex: 0,
        selectedInventoryId: "",
    };
}

function getInitialProgressState(): ProgressState {
    return {
        currentProgress: 0,
        title: "",
        maxProgress: 0,
        current: ""
    }
}

function getInitialState(usersLanguage: string): SurveyResponseState {
    return {
        pageDataLoaded: true,
        showErrorMessage: false,
        showNotFoundErrorScreen: false,
        dataLoaded: false,
        language: usersLanguage,
        progressState: getInitialProgressState(),
        coreData: null,
        selectedInventory: null,
        responseId: "",
        validationResult: {
            inventoryIds: [],
            optionalQuestionsInventoryIds: [],
            bomStatusTable: {},
            isOverallValid: false,
            result: ResultType.Ok,
            supplierQuestionnaireStatus: false,
            supplierQuestionnaireStatusList: []
        },
        navContext: getInitialNavContext(),
        surveyQuestionnairesList: [],
        questionnairesTypes: [],
        insActiveLanguage: getCultureName(),
        regulationManager: null
    };
}

function getCultureName(): string {
    let cultureName = localStorage.getItem("supplier-language");
    if (cultureName != null) {
        return cultureName;
    }
    cultureName = localStorage.getItem("toxnot-culture-name");
    if (cultureName != null) {
        return cultureName;
    }
    return "en"; //Default Value

    
}
// ===============================================================================

//
// private implementation & public helper functions
//

const serviceClient = new ToxnotServiceClient(); 

export const materiaListSizeGroupThreshold = 1; // This threshold defines the number of minimum inventories to pin the Group Material option into the side menu
export const materalListSizeBatchAnswerThreshold = 1; // This threshold defines the number of minimum inventories to pin the Bulk Input option into the side menu

// ===============================================================================

//
// Helper method that returns the supplier-level questionnaire in the response, if it exists, otherwise null
//
export const getSupplierLevelQuestionnaire = () => {
    return store.getState().surveyResponseState?.coreData?.response.supplierQuestionnaireData;
}

// ===============================================================================

//
// Returns the list of materials that were part of the original request, ignoring any groups.
//
export const getOriginalRequestMaterialInventoryList = (): SimpleInventory[] => {
    // TODO: large-surveys // using paging
    const coreData = store.getState().surveyResponseState?.coreData;

    if (!coreData) {
        return [];
    }

    return coreData.response.inventoryDataList
        .map(d => coreData.inventoryInfoTable[d.responderOwnedResponseInventoryId] ?? null)
        .filter(inv => inv !== null)
        .orderBy(inv => inv.name);
}

// ===============================================================================

//
// Returns the materials that the supplier is actually filling out in the response.
// This means root group materials & any un-grouped materials.
// If there are no groups or the response is for a revision request(the revision request is done at the inventory level, not at the group level,
// then this just returns the inventories in InventoryDataList
//
// The materials are returned in a sorted & stable order that they should always appear in
//
// See DbDataCollectionResponse.GetMaterialInventoryDataList()
//
export const getMainSupplierFacingMaterialInventoryList = (coreDataProvided?: CoreSurveyResponseData | null): SimpleInventory[] => {
    const coreData = coreDataProvided ?? store.getState().surveyResponseState?.coreData;

    if (!coreData) {
        return [];
    }

    const response = coreData.response;

    // Get inventory IDs in groups
    const inventoryIdsInGroups = new Set<string>(
        Object.values(response.groupedResponseInventories)
            .selectMany(group => group.responderOwnedResponseInventoryIdList)
    );

    // Create inventories list with grouping logic
    const groupedInventories = Object.values(response.groupedResponseInventories)
        .filter(g => g.groupInventoryId !== null)
        .map(g => ({
            inventoryId: g.groupInventoryId!,
            responseInventoryData: response.inventoryDataList.find(d =>
                g.responderOwnedResponseInventoryIdList.includes(d.responderOwnedResponseInventoryId)
            ) ?? null, // Return null if no match is found
            'hasGrouped': true
        }))
        .filter(item => item.responseInventoryData !== null); // Optionally filter out null items

    const ungroupedInventories = response.inventoryDataList
        .filter(d => !inventoryIdsInGroups.has(d.responderOwnedResponseInventoryId))
        .map(d => ({
            inventoryId: d.responderOwnedResponseInventoryId,
            responseInventoryData: d
        }));

    ungroupedInventories.map(d => coreData.inventoryInfoTable[d.inventoryId] ?? null)
        .filter(inv => inv !== null && coreData?.materialIdsDataToLoadList.some(i => i == inv.inventoryId))
        .orderBy(inv => inv.name);
    // Combine grouped inventories at the top and ungrouped inventories sorted by name
    const inventoriesList = [...groupedInventories, ...ungroupedInventories];

    // Map and filter final inventories
    const finalInventories = inventoriesList.map(d => ({
        ...coreData.inventoryInfoTable[d.inventoryId],
        inventoryValidationStatus: d.responseInventoryData?.inventoryValidationStatus as any
    })).filter(inv => inv !== null && coreData?.materialIdsDataToLoadList.some(i => i == inv.inventoryId));

    return finalInventories;
}

// ===============================================================================

export const coreValues = (coreData: CoreSurveyResponseData | null | undefined): PaginationDetails => {
    // Early check for null/undefined
    if (!coreData) {
        return {
            totalInventories: 0,
            totalInventoriesAfterGrouping: 0,
            totalPages: 1,
            hasGroups: false
        };
    }

    return {
        totalInventories: coreData.totalInventories,
        totalInventoriesAfterGrouping: coreData.totalInventoriesAfterGrouping,
        totalPages: coreData.totalPages,
        hasGroups: coreData.hasGroups
    };
};
// ===============================================================================

export async function getInventoryDetails(responseId: string, inventoryId: string) {
    const result = await serviceClient.getInventoryDetails(responseId, inventoryId);

    for (let questionnaire of result.data!.questionnaireList) {
        questionnaire.pages.forEach(p => {
            p.questionRegulatoryData = result.data?.questionRegulatoryData.filter(r => p.regulationId != null && p.regulationId != undefined && r.regulationId === p.regulationId) ?? [];
            p.regulatoryListIds = result.data?.applicableRegulatoryLists.firstOrDefault(r => p.regulationId != null && p.regulationId != undefined && r.regulationId === p.regulationId)?.regulationListIds ?? [];
        });
    }

    return result;
}

export const isLastItem = (): boolean => {
    const state = store.getState().surveyResponseState;
    const mainInventoryList = getMainSupplierFacingMaterialInventoryList(state.coreData);

    return (
        (
            (mainInventoryList.length === 0) &&
            (state.coreData!.response.supplierQuestionnaireData !== null) &&
            (state.coreData!.response.supplierQuestionnaireData?.questionnaire?.pages?.length > 0) &&
            (state.navContext.surveyQuestionnairePageIndex === (state.coreData!.response.supplierQuestionnaireData?.questionnaire?.pages?.length - 1))
        ) ||
        (
            (mainInventoryList.length > 0) &&
            (state.navContext.inventoryNavIndex === (mainInventoryList.length - 1)) &&
            ((state.surveyQuestionnairesList.length === 0 && (!state.selectedInventory?.hasBomPage || state.navContext.mainNavIndex == SurveyPageType.Bom)) ||
                (state.navContext.surveyQuestionnaireIndex === (state.surveyQuestionnairesList.length - 1)) &&
            (state.navContext.surveyQuestionnairePageIndex === (state.surveyQuestionnairesList[state.surveyQuestionnairesList.length - 1]?.pages.length - 1)))
        )
    );
}

// ===============================================================================

/**
 * Deserializes a SCIP article identifier from data stored in the backend (e.g. questionnaire data, summary data)

  * @param serializedData the data to deserialize. If it's empty, an ArticleIdentifier with default values is returned
 */
const deserializeArticleIdentifier = (serializedData: string): ArticleIdentifier =>
    serializedData.length === 0
        ? { identifierTypeId: DefaultScipArticleIdentifierId, value: "", typeOtherText: "" }
        : JSON.parse(serializedData) as ArticleIdentifier;

// ===============================================================================

export const doesQuestionNeedValueAndNotHaveValue = (question: QuestionnaireQuestion): boolean => {
    const values = getAllValuesForQuestionAndValueData(question);
    return values.length === 0;
}

// ===============================================================================

export const getAllValuesForQuestionAndValueData = (question: QuestionnaireQuestion): string[] => {
    if ([MetadataFieldTypes.MultiCheckbox, MetadataFieldTypes.Attestation].includes(question.type)) {
        return JSON.parse(nullOrEmptyCoalesce(question.value, "[]"))
            .map((s: number, index: number) => {
                if (s === 1) {
                    return (index < question.predefinedAnswers.length) ? question.predefinedAnswers[index].nativeAnswer : "";
                }
                else {
                    return "";
                }
            })
            .filter((s: number[]) => s.length > 0);
    }
    else if ([MetadataFieldTypes.ScipArticleCategory, MetadataFieldTypes.DataTable, MetadataFieldTypes.IngredientsTable].includes(question.type)) {
        return JSON.parse(nullOrEmptyCoalesce(question.value, "[]"));
    }
    else if (question.type === MetadataFieldTypes.MultiText) {
        const multiTextArr = JSON.parse(question.value);

        if (multiTextArr.length === 1 && multiTextArr[0] === "") {
            return [];
        }

        return multiTextArr;
    }
    else if (question.type === MetadataFieldTypes.ScipArticleIdentifier) {
        const articleIdentifier = deserializeArticleIdentifier(question.value);

        return articleIdentifier.value.length === 0 ? [] : [articleIdentifier.value];
    }
    else if (question.type === MetadataFieldTypes.Certification) {
        if (question.value.length === 0) {
            return [];
        }

        const certification = JSON.parse(question.value);

        if ((certification.name.length === 0) && (certification.certificationExternalId.length === 0)) {
            return [];
        }

        return [question.value];
    }
    else if (question.type === MetadataFieldTypes.CsiDivision) {
        if (question.value === ":") {
            return [];
        }
    }
    else {
        if (!question?.value?.length) {
            return [];
        }

        return [question.value];
    }

    return [];
}

// ===============================================================================

async function getMaterialInventoryData(responseInventoryId: string, selectedInventoryPageIndex: number | null = null)
{
    const coreData = store.getState().surveyResponseState?.coreData;

    if (!coreData) {
        return null;
    }

    const inventoryDataList = coreData.response.inventoryDataList;

    let responseInventoryData = inventoryDataList.firstOrDefault(d => d.responderOwnedResponseInventoryId === responseInventoryId);

    if (responseInventoryData === null) {
        const group = Object.values(coreData.response.groupedResponseInventories).firstOrDefault(g => g.groupInventoryId !== null && g.groupInventoryId === responseInventoryId);

        if (group !== null) {
            responseInventoryData = inventoryDataList.firstOrDefault(d => group.responderOwnedResponseInventoryIdList.includes(d.responderOwnedResponseInventoryId));
        }
    }

    if (responseInventoryData === null && selectedInventoryPageIndex != null)
    {
        const coreDataResult = await serviceClient.getCoreSurveyResponseData(coreData.response.responseId, selectedInventoryPageIndex);

        if (coreDataResult.data == null) {
            return;
        }

        responseInventoryData = coreDataResult.data.response.inventoryDataList.firstOrDefault(d => d.responderOwnedResponseInventoryId === responseInventoryId);
    }

    return responseInventoryData;
}

// ===============================================================================

// TODO: large-surveys // handle paging
async function updateValidationResult(getState: () => ApplicationState, dispatch: (action: KnownAction) => void, inventoryIds: string[], validationType: string | null = null) {
    let surveyState = getState().surveyResponseState;

    if (surveyState === null || surveyState === undefined) {
        return;
    }

    if (surveyState.coreData === null) {
        return;
    }

    let validationsResult: DataResult<SurveyResponseValidationResult> = await serviceClient.validateDataCollectionResponse(surveyState.responseId, inventoryIds, surveyState.coreData?.extensionRequest.inventoryIds, validationType);

    if (!validationsResult.success || !validationsResult.data) {
        return;
    }

    if (surveyState.selectedInventory?.inventoryId != null) {
        const result = await getInventoryDetails(surveyState.responseId, surveyState.selectedInventory?.inventoryId);

        if (!result.success || !result.data) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });
        } else {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    responseId: surveyState.responseId,
                    dataLoaded: true,
                    validationResult: {
                        ...validationsResult.data
                    },
                    selectedInventory: {
                        ...surveyState.selectedInventory,
                        validationType: result.data.validationType
                    },
                    coreData: {
                        ...surveyState.coreData,
                        response: {
                            ...surveyState.coreData?.response,
                            responseId: surveyState.responseId,
                            inventoryDataList: (surveyState.coreData?.response?.inventoryDataList || []).map<SurveyResponseInventoryData>(item => 
                                item.requestorTargetLibraryInventoryId === surveyState?.selectedInventory?.requestorOwnedInventoryId 
                                    ? { ...item, inventoryValidationStatus: String(result.data?.validationType || '') }
                                    : item
                            )
                        }
                    }
                }
            });
        }   
    }

    let updatedValidationResult: SurveyResponseValidationResult;
    

    if (inventoryIds && inventoryIds.length > 0) {
        updatedValidationResult = { ...surveyState.validationResult };

        inventoryIds.forEach(inventoryId => {
            let productValidationResult = validationsResult.data?.bomStatusTable[inventoryId];
            if (productValidationResult) {
                updatedValidationResult.bomStatusTable[inventoryId] = productValidationResult;
                updatedValidationResult.isOverallValid = validationsResult.data ? validationsResult.data.isOverallValid : false;
            }
        });
    } else {
        updatedValidationResult = validationsResult.data;
    }

    dispatch({
        type: SurveyResponseSetState,
        value: {
            validationResult: {
                ...updatedValidationResult
            }
        }
    });
}

async function updateValidationResultRemoveMaterials(getState: () => ApplicationState, dispatch: (action: KnownAction) => void, inventoryIds:string[]) {
    let surveyState = getState().surveyResponseState;

    if (surveyState === null || surveyState === undefined) {
        return;
    }

    let updatedValidationResult = { ...surveyState.validationResult };
    inventoryIds.forEach(i => delete updatedValidationResult.bomStatusTable[i]);

    let coreData = { ...surveyState.coreData };
    //if (coreData !== undefined && coreData?.extensionRequest?.inventoryIds?.length > 0) {
    //    coreData.extensionRequest. = inventoryIdsWithExtensionRequests.filter(i => !inventoryIds.some(id => id === i));
    //}

    dispatch({
        type: SurveyResponseSetState,
        value: {
            validationResult: {
                ...updatedValidationResult
            }
        }
    });
}

async function updateValidationResultAddMaterialsToGroup(getState: () => ApplicationState, dispatch: (action: KnownAction) => void, inventoryIds: string[], groupId: string ) {
    updateValidationResultRemoveMaterials(getState, dispatch, inventoryIds);
    updateValidationResult(getState, dispatch, [groupId]);
}

async function updateValidationResultRemoveGroup(getState: () => ApplicationState, dispatch: (action: KnownAction) => void, inventoryIds: string[], groupId: string | undefined) {
    if (groupId && groupId !== undefined) {
        updateValidationResultRemoveMaterials(getState, dispatch, [groupId]);
        updateValidationResult(getState, dispatch, inventoryIds);
    }
}

async function updateSupplierQuestionnaireValidationResult(getState: () => ApplicationState, dispatch: (action: KnownAction) => void) {
    let surveyState = getState().surveyResponseState;

    if (surveyState === null || surveyState === undefined) {
        return;
    }

    const validationsResult = await serviceClient.validateSupplierQuestionnaire(surveyState.responseId);

    if (!validationsResult.success || !validationsResult.data) {
        return;
    }

    let updatedValidationResult = { ...surveyState.validationResult };
    updatedValidationResult.supplierQuestionnaireStatusList = validationsResult.data.supplierQuestionnaireStatusList;
    updatedValidationResult.supplierQuestionnaireStatus = validationsResult.data.supplierQuestionnaireStatus;

    dispatch({
        type: SurveyResponseSetState,
        value: {
            validationResult: {
                ...updatedValidationResult
            }
        }
    });
}

// ===============================================================================

export const getCurrentPageForValidation = (inventory: BomStatusItem, currentQuestionnaireType: InventoryQuestionnaireType | undefined, currentPageType: QuestionnairePageType | string, currentPageTitle: string | null): PageQuestionnaireStatuses | null => {
    let page;

    if (currentQuestionnaireType === InventoryQuestionnaireType.AttestationQuestionnaire) {
        page = inventory?.questionnairesStatuses.attestationQuestionnaireStatus.firstOrDefault(p => p.page === currentPageType || p.page === currentPageTitle);
    } else if (currentQuestionnaireType === InventoryQuestionnaireType.CustomQuestionnaire) {
        page = inventory?.questionnairesStatuses.customQuestionnaireStatus.firstOrDefault(p => p.page === currentPageType || p.page === currentPageTitle);
    } else {
        page = inventory?.questionnairesStatuses.baseQuestionnaireStatus.firstOrDefault(p => p.page === currentPageType || p.page === currentPageTitle);
    }

    return page;
}

export const isOverallValid = (validationResult: SurveyResponseValidationResult) => {
    return validationResult.supplierQuestionnaireStatus && validationResult.isOverallValid;
        //Object.keys(validationResult.bomStatusTable).map(key => validationResult.bomStatusTable[key]).every(bs =>
        //    bs.questionnaireStatus && bs.attestationQuestionnaireStatus && bs.fieldsStatus && bs.circularityDataStatus 
        //    && bs.complianceDataStatus && bs.documentsDataStatus && bs.sustainabilityDataStatus);
}

export const markBomTableAsRequired = () => {
    const bomSpreasheetElem = $("#bom-editor-iframe").contents().find(".editor-root .product-spreadsheet-view");

    if (bomSpreasheetElem === null || bomSpreasheetElem === undefined || bomSpreasheetElem.length === 0) {
        window.addEventListener('message', event => bomIframeComm(event));
    } else {
        markBomCellsAsRequired();
    }
}

const BOM_DATA_LOADED = "BOM_DATA_LOADED";
const TRIGGER_VALIDATION = "TRIGGER_VALIDATION";

const bomIframeComm = (event: MessageEvent<any>) => {
    if (event.data === BOM_DATA_LOADED || event.data === TRIGGER_VALIDATION) {
        markBomCellsAsRequired();
    }
}

const markBomCellsAsRequired = () => {
    const bomSpreasheetElem = $("#bom-editor-iframe").contents().find(".editor-root .product-spreadsheet-view");

    const currentState = store.getState().surveyResponseState;

    const validationList = currentState.validationResult.bomStatusTable[currentState.selectedInventory!.inventoryId]?.inventoryBomValidation;

    validationList.forEach((col, index) => {
        // + 2 takes into account the spacer row and the header row in the bom table html structure
        bomSpreasheetElem.find(".product-spreadsheet-row").eq(col.rowIndex + 2).find(`.inventory-column-${col.columnId}`).addClass("has-error");
    });
}

const removeBomValidation = (validationList: BomValidationStatus[]) => {
    const bomSpreasheetElem = $("#bom-editor-iframe").contents().find(".editor-root .product-spreadsheet-view");

    validationList.forEach((col, index) => {
        // + 2 takes into account the spacer row and the header row in the bom table html structure
        bomSpreasheetElem.find(".product-spreadsheet-row").eq(col.rowIndex + 2).find(`.inventory-column-${col.columnId}`).removeClass("has-error");
    });
}

// ===============================================================================

function markQuestionsAsHiddenOrShown(questions: QuestionnaireQuestion[], responseUpdateCallback?: () => boolean[], clearValueOnHide?: boolean) {
    if (!responseUpdateCallback) {
        return;
    }
    
    const questionIndicesToHide = responseUpdateCallback();

    for (let i = 0; i < questions.length; ++i) {
        questions[i].hidden = questionIndicesToHide[i];
        questions[i].value = questionIndicesToHide[i] && clearValueOnHide ? "" : questions[i].value;
    }
}

// ===============================================================================

function updateQuestion(
    value: string, 
    valueComment: string, 
    questionIndex: number, 
    dispatch: (action: KnownAction) => void, 
    getState: () => ApplicationState, 
    files?: FileTableEntry[], 
    attachedFileIds?: string[],
    clearValueOnHide?: boolean
) {
    const surveyState = getState().surveyResponseState ?? getInitialState("en");

    if (surveyState.selectedInventory === null || surveyState.coreData === null) {
        return;
    }

    let currentPage = surveyState.surveyQuestionnairesList[surveyState.navContext.surveyQuestionnaireIndex].pages[surveyState.navContext.surveyQuestionnairePageIndex];

    let currentQuestion = currentPage.questions[questionIndex];

    currentQuestion.value = value;
    currentQuestion.valueComment = valueComment;

    if (currentQuestion.type == QuestionType.File ||currentQuestion.type == QuestionType.ImportCMRT || currentQuestion.requireFileAttachment || currentQuestion.type == "cmrt_details_json") {
        surveyState.surveyQuestionnairesList[surveyState.navContext.surveyQuestionnaireIndex].formatVersion = 4;
        currentQuestion.fileIds = attachedFileIds != undefined ? attachedFileIds : [];
    }

    if (surveyState.regulationManager !== null) {
        markQuestionsAsHiddenOrShown(currentPage.questions, () => surveyState.regulationManager!.getIndicesToHideOrShow(currentQuestion, questionIndex), clearValueOnHide);
    }
    
    if (ScipDataManager.isScipQuestion(currentQuestion)) {
        // TODO, this might change
        if (surveyState.navContext.currentlySelectedScipSubmissionData === null) {
            console.error("SCIP submission data was undefined");
            return;
        }

        ScipDataManager.modifyScipSubmissionDataBasedOnScipDataKey(currentQuestion, surveyState.navContext.currentlySelectedScipSubmissionData);
    }

    dispatch({
        type: SurveyResponseSetState,
        value: {
            ...surveyState,
            selectedInventory: {
                ...surveyState.selectedInventory,
                fileList: files || []
            },
            surveyQuestionnairesList: surveyState.surveyQuestionnairesList
        }
    });
}

//
// This function updates the state to have a new page index for the material list (which page
// of materials the user is viewing), as well as updates the index into the overall material list.
// It will then initiate the right dispatches and API calls to load in a new active material
// for edit.
//
// surveyQuestionnaireIndex, surveyQuestionnairePageIndex, and mainNavIndex are optional, and if
// provided, the state will be updated with their values as well.
//
async function setMaterialPageIndexAndInventoryNavIndex(
    surveyState: SurveyResponseState,
    newCurrentPageIndex: number,
    newInventoryNavIndex: number,
    dispatch: (action: KnownAction) => void,
    surveyQuestionnaireIndex?: number,
    surveyQuestionnairePageIndex?: number,
    mainNavIndex?: number,
    selectedInventoryPageIndex?: number
) {
    const mainInventoryList = getMainSupplierFacingMaterialInventoryList();

    // End of inventory list
    if (newInventoryNavIndex >= mainInventoryList.length) {
        return;
    }

    const newSelectedInventory = mainInventoryList[newInventoryNavIndex];

    // Do not fetch data if same inventory was previously selected
    if (newSelectedInventory.inventoryId === surveyState.selectedInventory?.inventoryId) {
        const navContext: NavContext = {
            mainNavIndex: mainNavIndex ?? SurveyPageType.Inventory,
            currentPageOfMaterialsIndex: newCurrentPageIndex,
            inventoryNavIndex: newInventoryNavIndex,
            surveyQuestionnaireIndex: surveyQuestionnaireIndex ?? -1,
            surveyQuestionnairePageIndex: surveyQuestionnairePageIndex ?? -1,
            previousPageStack: [],
            currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
            selectedInventoryPageIndex: selectedInventoryPageIndex ?? 0,
            selectedInventoryId: newSelectedInventory.inventoryId //update the selected inventory ID whenever user selects a new inventory
        };

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                navContext: navContext
            }
        });

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: newSelectedInventory.name,
                current: SurveyResponseProgressTextHelper.InventoryDetails,
                next: SurveyResponseProgressTextHelper.getInventoryFirstSubpageText(surveyState.selectedInventory, surveyState.insActiveLanguage),
                currentProgress: 0,
                maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(surveyState.selectedInventory)
            }
        });

        return;
    }

    dispatch({
        type: SurveyResponseSetState,
        value: {
            pageDataLoaded: false
        }
    });

    const result = await getInventoryDetails(surveyState.responseId, newSelectedInventory.inventoryId);

    if (!result.success || !result.data) {
        dispatch({
            type: SurveyResponseSetState,
            value: {
                showErrorMessage: true,
                dataLoaded: true
            }
        });

        return;
    }

    dispatch({
        type: SurveyResponseSetState,
        value: {
            pageDataLoaded: true,
            selectedInventory: {
                ...result.data,
                inventoryId: newSelectedInventory.inventoryId
            },
            navContext: {
                mainNavIndex: mainNavIndex ?? SurveyPageType.Inventory,
                currentPageOfMaterialsIndex: newCurrentPageIndex,
                inventoryNavIndex: newInventoryNavIndex,
                surveyQuestionnaireIndex: surveyQuestionnaireIndex ?? -1,
                surveyQuestionnairePageIndex: surveyQuestionnairePageIndex ?? -1,
                previousPageStack: [],
                currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
                selectedInventoryPageIndex: selectedInventoryPageIndex ?? 0,
                selectedInventoryId: newSelectedInventory.inventoryId
            },
            surveyQuestionnairesList: result.data.questionnaireList,
            questionnairesTypes: result.data.questionnairesTypes
        }
    });

    dispatch({
        type: SurveyResponseSetProgressState,
        value: {
            title: newSelectedInventory.name,
            current: SurveyResponseProgressTextHelper.InventoryDetails,
            next: SurveyResponseProgressTextHelper.getInventoryFirstSubpageText(result.data, surveyState.insActiveLanguage),
            currentProgress: 0,
            maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(result.data)
        }
    });
}

function getSelectedInventoryId(responseId: string) {
    const currentNavContext: NavContext = JSON.parse(localStorage.getItem(`${LocalStorageNavContextKey}-${responseId}`) ?? "null");
    var selectedInventoryId = null;

    if (currentNavContext) {
        if (currentNavContext.selectedInventoryId !== null && currentNavContext.selectedInventoryId !== undefined && currentNavContext.selectedInventoryId !== "") {
            selectedInventoryId = currentNavContext.selectedInventoryId;
        }
    }

    return selectedInventoryId;
}

// ===============================================================================

//
// Action creators
//

export const actionCreators = {
    setSurveyResponseProgress: (value: Partial<ProgressState>) => ({
        type: SurveyResponseSetProgressState,
        value: value
    }),

    setExtensionRequestAndValidateProduct: (extensionRequest: ExtensionRequestModel): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;
        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                coreData: {
                    ...(surveyState?.coreData!),
                    extensionRequest: extensionRequest
                }
            }
        });

        if (surveyState?.selectedInventory?.inventoryId) {
            if (surveyState?.navContext.mainNavIndex === SurveyPageType.Bom && extensionRequest.inventoryIds.some(i => i === surveyState?.selectedInventory?.inventoryId)) {
                let currentBomValidation = surveyState.validationResult.bomStatusTable[surveyState?.selectedInventory?.inventoryId]?.inventoryBomValidation;
                removeBomValidation(currentBomValidation);
            }

            updateValidationResult(getState, dispatch, [surveyState?.selectedInventory?.inventoryId]);
        }
    },

    setSurveyResponseState: (value: Partial<SurveyResponseState>) => ({
        type: SurveyResponseSetState,
        value: value
    }),

    updateSupplierQuestionnaire: (value: string, questionIndex: number, files?: FileTableEntry[], attachedFileIds?: string[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) ||
            (surveyState.coreData === null) ||
            (surveyState.coreData.response.supplierQuestionnaireData === null)) {
            return;
        }

        const supplierLevelQuestionnaire = getSupplierLevelQuestionnaire();

        if (supplierLevelQuestionnaire === null || supplierLevelQuestionnaire === undefined) {
            return;
        }

        supplierLevelQuestionnaire.files = files;
        const currentPage = supplierLevelQuestionnaire.questionnaire.pages[surveyState.navContext.surveyQuestionnairePageIndex];
        const currentQuestion = currentPage.questions[questionIndex];

        currentQuestion.value = value;

        if ((currentQuestion.type == QuestionType.File || currentQuestion.requireFileAttachment || currentQuestion.type == QuestionType.ImportCMRT) && attachedFileIds) {
            currentQuestion.fileIds = attachedFileIds != undefined ? attachedFileIds : [];
        }

        if (surveyState.regulationManager !== null) {
            markQuestionsAsHiddenOrShown(currentPage.questions, () => surveyState.regulationManager!.getIndicesToHideOrShow(currentQuestion, questionIndex));
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                coreData: {
                    ...(surveyState.coreData!),
                    allSupplierFiles: files || [],
                    response: {
                        ...(surveyState.coreData.response),
                        supplierQuestionnaireData: {
                            ...(surveyState.coreData.response.supplierQuestionnaireData)
                        }
                    }
                }
            }
        });
    },

    setCurrentPageNextClicked: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (surveyState === undefined) {
            return;
        }

        const questionnaireList = surveyState.surveyQuestionnairesList;
        const currentPage = questionnaireList[surveyState.navContext.surveyQuestionnaireIndex]?.pages[surveyState.navContext.surveyQuestionnairePageIndex];

        currentPage.nextClicked = true;

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                surveyQuestionnairesList: questionnaireList
            }
        });
    },
    
    setBomPageNextClicked: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (surveyState !== undefined) {
            const selectedInventory = surveyState.selectedInventory;

            if (selectedInventory !== null && selectedInventory !== undefined) {
                selectedInventory.bomNextClicked = true;

                dispatch({
                    type: SurveyResponseSetState,
                    value: {
                        ...surveyState,
                        selectedInventory: selectedInventory
                    }
                });
            }
        }
    },

    setCurrentSupplierPageNextClicked: (pageTitle: string | undefined): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) ||
            (surveyState.coreData === null) ||
            (surveyState.coreData.response.supplierQuestionnaireData === null)) {
            return;
        }

        const questionnaireList = getSupplierLevelQuestionnaire();
        const currentPage = questionnaireList?.questionnaire.pages.firstOrDefault(p => p.title === pageTitle);

        if (currentPage !== null && currentPage !== undefined && questionnaireList !== undefined && questionnaireList !== null) {
            currentPage.nextClicked = true;

            dispatch({
                type: SurveyResponseSetState,
                value: {
                    ...surveyState,
                    coreData: {
                        ...(surveyState.coreData!),
                        response: {
                            ...(surveyState.coreData.response),
                            supplierQuestionnaireData: questionnaireList
                        }
                    }
                }
            });
        }
    },

    updateCurrentlySelectedScipSubmissionsData: (scipSubmissionData: ScipSubmissionData): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");
        const newQuestionnairesList = [...surveyState.surveyQuestionnairesList];
        const questions = newQuestionnairesList[surveyState.navContext.surveyQuestionnaireIndex].pages[surveyState.navContext.surveyQuestionnairePageIndex].questions;

        if (!surveyState.selectedInventory || !surveyState.coreData || !scipSubmissionData) {
            return;
        }

        ScipDataManager.fillScipQuestionsWithScipData(questions, scipSubmissionData);

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                navContext: {
                    ...surveyState.navContext,
                    currentlySelectedScipSubmissionData: scipSubmissionData
                }
            }
        });

        for (let i = 0; i < questions.length; ++i) {
            updateQuestion(
                questions[i].value,
                questions[i].valueComment,
                i,
                dispatch,
                getState
            );
        }
    },

    resetSelectedScipSubmissionsData: (scipSubmissionData: ScipSubmissionData, questionnaireList: Questionnaire[], currentSurveyQuestionnaireIndex: number, currentSurveyQuestionnairePageIndex: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");
        const newQuestionnairesList = [...questionnaireList];
        const questions = newQuestionnairesList[currentSurveyQuestionnaireIndex]?.pages[currentSurveyQuestionnairePageIndex].questions;

        if (!surveyState.selectedInventory || !surveyState.coreData || !scipSubmissionData) {
            return;
        }

        ScipDataManager.fillScipQuestionsWithScipData(questions, scipSubmissionData);

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                selectedInventory: {
                    ...surveyState.selectedInventory,
                    questionnaireList: newQuestionnairesList
                },
                navContext: {
                    ...surveyState.navContext,
                    currentlySelectedScipSubmissionData: scipSubmissionData
                }
            }
        });
    },

    updateQuestion: (value: string, valueComment: string, questionIndex: number, files?: FileTableEntry[], attachedFileIds?: string[], clearValueOnHide?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        updateQuestion(value, valueComment, questionIndex, dispatch, getState, files, attachedFileIds, clearValueOnHide);
    },

    addSurveyInventory: (inventory: SimpleInventory): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");
        //dispatch({ type: SurveyResponseSetState, value: { dataLoaded: false } });

        surveyState.coreData?.response.inventoryDataList.push({
            bomDataHasBeenAdded: false,
            responderOwnedResponseInventoryId: inventory.inventoryId,
            supplierLibraryInventoryId: inventory.inventoryId,
            publicInventoryId: null,
            requestorOwnedResponseInventoryId: null,
            requestorTargetLibraryInventoryId: null,
            questionnaireHasBeenWalkedThrough: false,
            revisionRequest: null,
            sharedMaterialId: null,
            metadata: {
                questionnairePageIndicesVisited: [],
                bomEditorPageVisited: false
            },
            inventoryValidationStatus: null
        });

        if (surveyState.coreData) {
            //If user has added a new material then increment the counters by 1.
            surveyState.coreData.totalInventories += 1;
            surveyState.coreData.totalInventoriesAfterGrouping += 1;
            surveyState.coreData.totalPages = Math.ceil(surveyState.coreData.totalInventories / surveyResponseMaterialsPerPage);
        }

        if (surveyState?.coreData?.inventoryInfoTable) {
            surveyState.coreData.inventoryInfoTable[inventory.inventoryId] = inventory;
            surveyState.coreData.materialIdsDataToLoadList.push(inventory.inventoryId)

            dispatch({
                type: SurveyResponseSetState,
                value: {
                    selectedInventory: null,
                    progressState: surveyState.progressState,
                    coreData: {
                        ...surveyState.coreData,
                        inventoryInfoTable: {
                            ...surveyState.coreData.inventoryInfoTable,
                            [inventory.inventoryId]: inventory
                        },
                        materialIdsDataToLoadList: [...surveyState.coreData.materialIdsDataToLoadList]
                    },
                    navContext: { ...surveyState.navContext }
                }
            });
        }

    },

    setGroupsState: (newGroups: GroupedResponseInventories): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");

        const newInventoryInfoTable = { ...surveyState.coreData!.inventoryInfoTable };
        const newMaterialIdsDataToLoadList = [...surveyState.coreData!.materialIdsDataToLoadList];

        for (let groupInventoryName of Object.keys(newGroups)) {
            const group = newGroups[groupInventoryName];

            if (group.groupInventoryId !== null) {
                newInventoryInfoTable[group.groupInventoryId] = {
                    ownerId: "",
                    inventoryId: group.groupInventoryId,
                    name: groupInventoryName,
                    description: "",
                    supplierName: "",
                    productNumber: "",
                    supplierPartNumber: "",
                    supplierId: null,
                    plantName: "",
                    isComponent: true
                };

                if (!newMaterialIdsDataToLoadList.some(i => i == group.groupInventoryId)) {
                    newMaterialIdsDataToLoadList.push(group.groupInventoryId);
                }
            }
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                selectedInventory: null,
                coreData: {
                    ...(surveyState.coreData!),
                    response: {
                        ...(surveyState.coreData!.response),
                        groupedResponseInventories: newGroups ?? []
                    },
                    materialIdsDataToLoadList: newMaterialIdsDataToLoadList,
                    inventoryInfoTable: newInventoryInfoTable
                }
            }
        });
    },

    deleteSurveyInventory: (inventoryId: string, isGroup: boolean = false): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");

        if(surveyState?.coreData?.inventoryInfoTable) {
            surveyState.coreData.response.inventoryDataList = surveyState.coreData?.response.inventoryDataList.filter(x => x.responderOwnedResponseInventoryId !== inventoryId && x.supplierLibraryInventoryId !== inventoryId);

            delete surveyState.coreData.inventoryInfoTable[inventoryId];
            delete surveyState.validationResult.bomStatusTable[inventoryId];
            surveyState.coreData.materialIdsDataToLoadList = surveyState.coreData.materialIdsDataToLoadList.filter(invId => invId !== inventoryId)
            let totalInventories = surveyState.coreData.totalInventories - 1;
            let totalInventoriesAfterGrouping = surveyState.coreData.totalInventoriesAfterGrouping - 1;
            let currentPage = surveyState.navContext.currentPageOfMaterialsIndex;
            let lastPage = Math.ceil(surveyState.coreData.totalInventoriesAfterGrouping / surveyResponseMaterialsPerPage) - 1;
            let isLastPage = currentPage === lastPage;
            
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    selectedInventory: null,
                    progressState: surveyState.progressState,
                    coreData: {
                        ...surveyState.coreData,
                        inventoryInfoTable: {
                            ...surveyState.coreData.inventoryInfoTable
                        },
                        materialIdsDataToLoadList: [...surveyState.coreData.materialIdsDataToLoadList],
                        totalInventories: totalInventories,
                        totalInventoriesAfterGrouping: totalInventoriesAfterGrouping
                    },
                    navContext: { ...surveyState.navContext },
                    validationResult: surveyState.validationResult
                }
            });
            
            
            if(totalInventoriesAfterGrouping % surveyResponseMaterialsPerPage === 0 && surveyState.navContext.currentPageOfMaterialsIndex > 0 && isLastPage && !isGroup){
                //If there are no more inventories on the current page then navigate user to the previous page if any. 
                await actionCreators.getPaginatedSurveyData(surveyState.navContext.currentPageOfMaterialsIndex - 1)(dispatch, getState);
            }
        }
    },

    setSurveySelectedInventoryProductDetails: (name: string, description: string, fileId: string ): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState().surveyResponseState ?? getInitialState("en");

        if (state.coreData === null) {
            return;
        }

        if (state.selectedInventory === null) {
            return;
        }

        const selectedInventory = { ...state.selectedInventory };

        const progressState = {
            ...state.progressState,
            title: name
        };

        selectedInventory.name = name;
        selectedInventory.description = description;
        selectedInventory.photoFileId = fileId;

        const newInventoryInfo: SimpleInventory = {
            ...(state.coreData!.inventoryInfoTable[selectedInventory.inventoryId])!,
            name: name,
            description: description,
        };

        // TODO: this needs to actually write these changes to the backend via api

        dispatch({
            type: SurveyResponseSetState,
            value: {
                selectedInventory: selectedInventory,
                progressState: progressState,
                coreData: {
                    ...state.coreData,
                    inventoryInfoTable: {
                        ...state.coreData.inventoryInfoTable,
                        [selectedInventory.inventoryId]: newInventoryInfo
                    }
                }
            }
        });
    },

    setNavigationContext: (pageType: SurveyPageType, inventoryIndex: number, questionnaireIndex: number, questionnairePageIndex: number, previousPageStack: number[] = []): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) || (surveyState.coreData === null)) {
            return;
        }

        const navContext: NavContext = {
            mainNavIndex: pageType,
            currentPageOfMaterialsIndex: surveyState.navContext.currentPageOfMaterialsIndex,
            inventoryNavIndex: inventoryIndex,
            surveyQuestionnaireIndex: questionnaireIndex,
            surveyQuestionnairePageIndex: questionnairePageIndex,
            previousPageStack: previousPageStack,
            currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
            selectedInventoryPageIndex: surveyState.navContext.selectedInventoryPageIndex,
            selectedInventoryId: surveyState.navContext.selectedInventoryId,
        };

        dispatch({
            type: SurveyResponseSetState,
            value: {
                ...surveyState,
                navContext: navContext
            }
        });

        const inventory = surveyState.selectedInventory;

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: inventory?.name,
                current: surveyState.surveyQuestionnairesList[navContext.surveyQuestionnaireIndex]?.pages[navContext.surveyQuestionnairePageIndex].languageToTitleMap[surveyState.insActiveLanguage] ?? surveyState.surveyQuestionnairesList[navContext.surveyQuestionnaireIndex]?.pages[navContext.surveyQuestionnairePageIndex].title ?? SurveyResponseProgressTextHelper.mapPageTypeToText[pageType],
                next: SurveyResponseProgressTextHelper.getNextTextByNavigationContext(inventory, navContext, surveyState.coreData, surveyState.insActiveLanguage),
                currentProgress: SurveyResponseProgressTextHelper.getCurrentInventoryStep(inventory, navContext),
                maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(inventory)
            }
        });
    },

    setMainNavPage: (pageType: SurveyPageType, surveyQuestionnaireIndex?: number, surveyQuestionnairePageIndex?: number, previousPageStack: number[] = []): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) || (surveyState.coreData === null)) {
            return;
        }

        const navContext: NavContext = {
            mainNavIndex: pageType,
            currentPageOfMaterialsIndex: surveyState.navContext.currentPageOfMaterialsIndex,
            inventoryNavIndex: -1,
            surveyQuestionnaireIndex: surveyQuestionnaireIndex ?? -1,
            surveyQuestionnairePageIndex: surveyQuestionnairePageIndex ?? -1,
            previousPageStack: previousPageStack,
            currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
            selectedInventoryPageIndex: surveyState.navContext.selectedInventoryPageIndex,
            selectedInventoryId: surveyState.navContext.selectedInventoryId,
        };

        dispatch({
            type: SurveyResponseSetState,
            value: {
                navContext: navContext,
                selectedInventory: null
            }
        });

        const pagesOrder = SurveyResponseProgressTextHelper.topLevelPagesOrder(surveyState.coreData);
        const index = pagesOrder.indexOf(pageType);

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: SurveyResponseProgressTextHelper.mapPageTypeToText[pageType],
                current: SurveyResponseProgressTextHelper.mapPageTypeToText[pageType],
                next: SurveyResponseProgressTextHelper.mapPageTypeToText[pagesOrder[index + 1]],
                currentProgress: 0,
                maxProgress: 0
            }
        });
    },

    setSurveySelectedSupplierQuestionnaire: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) || (surveyState.coreData === null)) {
            return;
        }

        const supplierLevelQuestionnaire = getSupplierLevelQuestionnaire();

        if (supplierLevelQuestionnaire === null) {
            return;
        }

        const navContext: NavContext = {
            mainNavIndex: SurveyPageType.SupplierQuestionnaire,
            currentPageOfMaterialsIndex: surveyState.navContext.currentPageOfMaterialsIndex,
            inventoryNavIndex: -1,
            surveyQuestionnaireIndex: 0,
            surveyQuestionnairePageIndex: 0,
            previousPageStack: [],
            currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
            selectedInventoryPageIndex: surveyState.navContext.selectedInventoryPageIndex,
            selectedInventoryId: surveyState.navContext.selectedInventoryId
        };

        dispatch({
            type: SurveyResponseSetState,
            value: {
                navContext: navContext
            }
        });

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: SurveyResponseProgressTextHelper.SurveyInfo,
                current: SurveyResponseProgressTextHelper.SurveyInfo,
                next: SurveyResponseProgressTextHelper.getNextTextByNavigationContext(null, navContext, surveyState.coreData, surveyState.insActiveLanguage),
                currentProgress: 0,
                maxProgress: 0
            }
        });
    },

    setSurveyBomPage: (invIndex: number): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === undefined) || (surveyState.coreData === null)) {
            return;
        }

        const navContext: NavContext = {
            mainNavIndex: SurveyPageType.Bom,
            currentPageOfMaterialsIndex: surveyState.navContext.currentPageOfMaterialsIndex,
            inventoryNavIndex: invIndex,
            surveyQuestionnaireIndex: -1,
            surveyQuestionnairePageIndex: 1,
            previousPageStack: [],
            currentlySelectedScipSubmissionData: surveyState.navContext.currentlySelectedScipSubmissionData,
            selectedInventoryPageIndex: surveyState.navContext.selectedInventoryPageIndex,
            selectedInventoryId: surveyState.navContext.selectedInventoryId
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                navContext: navContext
            }
        });

        const inventory = surveyState.selectedInventory;

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: inventory?.name,
                current: SurveyResponseProgressTextHelper.BOM,
                next: SurveyResponseProgressTextHelper.getNextTextByNavigationContext(inventory, navContext, surveyState.coreData, surveyState.insActiveLanguage),
                currentProgress: SurveyResponseProgressTextHelper.getCurrentInventoryStep(inventory, navContext),
                maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(inventory)
            }
        });
    },

    setSurveySelectedInventory: (invIndex: number, surveyQuestionnaireIndex?: number, surveyQuestionnairePageIndex?: number, mainNavIndex?: number): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");
        
        await setMaterialPageIndexAndInventoryNavIndex(
            surveyState,
            surveyState.navContext.currentPageOfMaterialsIndex,
            invIndex,
            dispatch,
            surveyQuestionnaireIndex,
            surveyQuestionnairePageIndex,
            mainNavIndex,
            surveyState.navContext.currentPageOfMaterialsIndex
        );
    },

    updateQuestionRequired: (modifiedQuestionnairesList: Questionnaire[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        let surveyState = getState().surveyResponseState;

        if (surveyState !== null && surveyState !== undefined) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    surveyQuestionnairesList: modifiedQuestionnairesList
                }
            });
        }
    },

    updateValidationResult: (inventoryIds: string[], validationType: string | null = null): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        await updateValidationResult(getState, dispatch, inventoryIds, validationType);
    },

    updateSupplierQuestionnaireValidationResult: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        await updateSupplierQuestionnaireValidationResult(getState, dispatch);
    },

    updateValidationResultRemoveGroup: (inventoryIds: string[], groupId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        await updateValidationResultRemoveGroup(getState, dispatch, inventoryIds, groupId);
    },

    updateValidationResultAddMaterialsToGroup: (inventoryIds: string[], groupId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        await updateValidationResultAddMaterialsToGroup(getState, dispatch, inventoryIds, groupId);
    },

    updateBaseQuestionnairePageHasBeenVisited: (questionnaireIndex: number, pageIndex: number, selectedInventoryPageIndex: number, set: boolean = true): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        let surveyState = getState().surveyResponseState;

        if (surveyState === null || surveyState === undefined) {
            return;
        }

        const inventoryId = surveyState?.selectedInventory?.inventoryId;

        if (!inventoryId) {
            return;
        }

        const inventoryData = await getMaterialInventoryData(inventoryId, selectedInventoryPageIndex);

        if (!inventoryData) {
            console.error("Unable to find the inventory data.");
            return;
        }

        if (questionnaireIndex >= inventoryData.metadata.questionnairePageIndicesVisited.length) {
            const questionniare = surveyState.surveyQuestionnairesList[questionnaireIndex];

            inventoryData.metadata.questionnairePageIndicesVisited = Array(questionniare.pages.length).fill(0);
        }

        const getIntForSet = (set: boolean) => set ? 1 : 0;

        if (inventoryData.metadata.questionnairePageIndicesVisited[pageIndex] === getIntForSet(set)) {
            return;
        }

        inventoryData.metadata.questionnairePageIndicesVisited[pageIndex] = getIntForSet(set);

        let apiReult = await serviceClient.dataCollectionResponseInventoryDataUpdateMetadata(surveyState.responseId, inventoryData);

        if (!apiReult.success) {
            getState().appServices?.displayApiErrorMessage();
            return;
        }

        await updateValidationResult(getState, dispatch, [inventoryId]);
    },

    updateBomEditorPageVisited: (set: boolean = true): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        let surveyState = getState().surveyResponseState;

        if (surveyState === null || surveyState === undefined) {
            return;
        }

        const inventoryId = surveyState?.selectedInventory?.inventoryId;

        if (!inventoryId) {
            return;
        }

        const inventoryData = await getMaterialInventoryData(inventoryId);

        if (inventoryData === null || inventoryData === undefined) {
            console.error("Unable to find the inventory data.");
            return;
        }

        if (inventoryData.metadata.bomEditorPageVisited === set) {
            return;
        }

        inventoryData.metadata.bomEditorPageVisited = set;

        let apiReult = await serviceClient.dataCollectionResponseInventoryDataUpdateMetadata(surveyState.responseId, inventoryData);

        if (!apiReult.success) {
            getState().appServices?.displayApiErrorMessage();
            return;
        }

        await updateValidationResult(getState, dispatch, [inventoryId]);
    },

    getInitialSurveyData: (responseId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === null || surveyState === undefined) || surveyState.dataLoaded) {
            return;
        }

        let page = 0;

        const currentNavContext: NavContext = JSON.parse(localStorage.getItem(`${LocalStorageNavContextKey}-${responseId}`) ?? "null");
        if (currentNavContext !== null) {
            page = currentNavContext.currentPageOfMaterialsIndex;
        }

        const coreDataResult = await serviceClient.getCoreSurveyResponseData(responseId, page);

        if (!coreDataResult.success || !coreDataResult.data) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });

            return;
        }

        var currentPageOfInventoryIds = getMainSupplierFacingMaterialInventoryList(coreDataResult.data)
            .map(inv => inv.inventoryId);

        // Add the selectedInventoryId to the array if does not exist already.
        var selectedInventoryId = getSelectedInventoryId(responseId);
        if (selectedInventoryId != null) {
            if (!currentPageOfInventoryIds.includes(selectedInventoryId)) {
                currentPageOfInventoryIds.push(selectedInventoryId);
            }
        }

        const validationsResult = await serviceClient.validateDataCollectionResponse(responseId, currentPageOfInventoryIds, coreDataResult.data.extensionRequest.inventoryIds);

        if (!validationsResult.success || !validationsResult.data) {
            dispatch({
                type: SurveyResponseSetState, 
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });

            return;
        }

        const prevNavContext: NavContext = JSON.parse(localStorage.getItem(`${LocalStorageNavContextKey}-${coreDataResult.data.response.responseId}`) ?? "null");

        if (prevNavContext !== null && prevNavContext.inventoryNavIndex > -1) {
            const mainInventoryList = getMainSupplierFacingMaterialInventoryList(coreDataResult.data);

            // If an inventory has already been selected and stored in the surveyState, that inventory will be used. And
            // if no inventory is stored, the first inventory in the list will be selected, following the existing logic.
            const newSelectedInventory = mainInventoryList[mainInventoryList.length > prevNavContext?.inventoryNavIndex ? prevNavContext?.inventoryNavIndex : 0];
            let selectedInventoryId = newSelectedInventory?.inventoryId;
            //if user has any selected inventory before the page refresh, it'll render that selected inventory
            if (currentNavContext.selectedInventoryId !== null && currentNavContext.selectedInventoryId !== undefined && currentNavContext.selectedInventoryId !== "") {
                selectedInventoryId = currentNavContext.selectedInventoryId;
            }

            if (newSelectedInventory === undefined || newSelectedInventory === null) {
                dispatch({
                    type: SurveyResponseSetState,
                    value: {
                        responseId: responseId,
                        dataLoaded: true,
                        coreData: coreDataResult.data,
                        validationResult: {
                            ...validationsResult.data
                        },
                        navContext: {
                            ...prevNavContext,
                            currentPageOfMaterialsIndex: 0,
                            currentlySelectedScipSubmissionData: null
                        },
                    }
                });
                return;
            }

            const result = await getInventoryDetails(coreDataResult.data.response.responseId, selectedInventoryId);

            if (!result.success || !result.data) {
                dispatch({
                    type: SurveyResponseSetState,
                    value: {
                        showErrorMessage: true,
                        dataLoaded: true
                    }
                });

                return;
            }

            dispatch({
                type: SurveyResponseSetState,
                value: {
                    responseId: responseId,
                    dataLoaded: true,
                    coreData: coreDataResult.data,
                    validationResult: {
                        ...validationsResult.data
                    },
                    selectedInventory: {
                        ...result.data,
                        inventoryId: selectedInventoryId
                    },
                    navContext: {
                        ...prevNavContext,
                        currentPageOfMaterialsIndex: page,
                        currentlySelectedScipSubmissionData: null
                    },
                    surveyQuestionnairesList: result.data.questionnaireList,
                    questionnairesTypes: result.data.questionnairesTypes
                }
            });

            const inventory = result.data;

            dispatch({
                type: SurveyResponseSetProgressState,
                value: {
                    title: inventory?.name,
                    current: surveyState.surveyQuestionnairesList[prevNavContext.surveyQuestionnaireIndex]?.pages[prevNavContext.surveyQuestionnairePageIndex].languageToTitleMap[surveyState.insActiveLanguage] ?? surveyState.surveyQuestionnairesList[prevNavContext.surveyQuestionnaireIndex]?.pages[prevNavContext.surveyQuestionnairePageIndex].title ?? SurveyResponseProgressTextHelper.mapPageTypeToText[prevNavContext.mainNavIndex],
                    next: SurveyResponseProgressTextHelper.getNextTextByNavigationContext(inventory, prevNavContext, coreDataResult.data, surveyState.insActiveLanguage),
                    currentProgress: SurveyResponseProgressTextHelper.getCurrentInventoryStep(inventory, prevNavContext),
                    maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(inventory)
                }
            });

            return;
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                responseId: responseId,
                dataLoaded: true,
                coreData: coreDataResult.data,
                validationResult: {
                    ...validationsResult.data
                },
                navContext: prevNavContext ?? getInitialNavContext()
            }
        });

        const pagesOrder = SurveyResponseProgressTextHelper.topLevelPagesOrder(coreDataResult.data);

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: "Digital Product Passport",
                current: prevNavContext !== null ? SurveyResponseProgressTextHelper.mapPageTypeToText[prevNavContext.mainNavIndex] : SurveyResponseProgressTextHelper.Instructions,
                next: SurveyResponseProgressTextHelper.mapPageTypeToText[pagesOrder[pagesOrder.indexOf(prevNavContext !== null ? prevNavContext.mainNavIndex : SurveyPageType.Instructions) + 1]]
            }
        });
    },

    getPaginatedSurveyData: (page: number, showSelectedInventory: boolean = false, invIndex: number = -1, surveyQuestionnaireIndex: number = -1, surveyQuestionnairePageIndex: number = -1,  mainNavIndex: number = -1): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === null || surveyState === undefined)) {
            return;
        }

        dispatch({ type: SurveyResponseSetState, value: { dataLoaded: false } });
        // Fetch data for the new page from the backend
        const responseId = surveyState.responseId;
        
        const coreDataResult = await serviceClient.getCoreSurveyResponseData(responseId, page);
        if (!coreDataResult.success || !coreDataResult.data) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });

            return;
        }

        var currentPageOfInventoryIds = getMainSupplierFacingMaterialInventoryList(coreDataResult.data)
            .map(inv => inv.inventoryId);

        // Add the selectedInventoryId to the array if does not exist already.
        var selectedInventoryId = getSelectedInventoryId(responseId);
        if (selectedInventoryId != null) {
            if (!currentPageOfInventoryIds.includes(selectedInventoryId)) {
                currentPageOfInventoryIds.push(selectedInventoryId);
            }
        }

        const validationsResult = await serviceClient.validateDataCollectionResponse(responseId, currentPageOfInventoryIds, coreDataResult.data.extensionRequest.inventoryIds);

        if (!validationsResult.success || !validationsResult.data) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });

            return;
        }

        const prevNavContext: NavContext = JSON.parse(localStorage.getItem(`${LocalStorageNavContextKey}-${coreDataResult.data.response.responseId}`) ?? "null");
        if (prevNavContext !== null) {
            prevNavContext.currentPageOfMaterialsIndex = page;
            if(showSelectedInventory){
                prevNavContext.surveyQuestionnaireIndex = surveyQuestionnaireIndex ?? -1;
                prevNavContext.surveyQuestionnairePageIndex = surveyQuestionnairePageIndex ?? -1;
            }
            //prevNavContext.mainNavIndex = mainNavIndex ?? SurveyPageType.Inventory;
        }

        if (showSelectedInventory)
        {
            const mainInventoryList = getMainSupplierFacingMaterialInventoryList(coreDataResult.data);

            // If an inventory has already been selected and stored in the surveyState, that inventory will be used. And
            // if no inventory is stored, the first inventory in the list will be selected, following the existing logic.
            const newSelectedInventory = mainInventoryList[invIndex];

            if (newSelectedInventory === undefined || newSelectedInventory === null) {
                return;
            }

            const result = await getInventoryDetails(coreDataResult.data.response.responseId, newSelectedInventory.inventoryId);

            if (!result.success || !result.data) {
                dispatch({
                    type: SurveyResponseSetState,
                    value: {
                        showErrorMessage: true,
                        dataLoaded: true
                    }
                });

                return;
            }

            //prevNavContext.selectedInventoryPageIndex = page;
            /*if (invIndex === 0) { //first inventory: if user goes to next page, selectedInventoryPageIndex should be incremented
                prevNavContext.selectedInventoryPageIndex += 1;
            } else if (invIndex === surveyResponseMaterialsPerPage - 1) { //last inventory: if user goes to previous page, selectedInventoryPageIndex should be decremented
                prevNavContext.selectedInventoryPageIndex -= 1;
            }*/
            
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    responseId: responseId,
                    dataLoaded: true,
                    coreData: coreDataResult.data,
                    validationResult: {
                        ...validationsResult.data
                    },
                    selectedInventory: {
                        ...result.data,
                        inventoryId: newSelectedInventory.inventoryId
                    },
                    navContext: {
                        ...prevNavContext,
                        selectedInventoryPageIndex: page,
                        inventoryNavIndex: invIndex,
                        currentlySelectedScipSubmissionData: null
                    },
                    surveyQuestionnairesList: result.data.questionnaireList,
                    questionnairesTypes: result.data.questionnairesTypes
                }
            });

            const inventory = result.data;

            dispatch({
                type: SurveyResponseSetProgressState,
                value: {
                    title: inventory?.name,
                    current: surveyState.surveyQuestionnairesList[prevNavContext.surveyQuestionnaireIndex]?.pages[prevNavContext.surveyQuestionnairePageIndex].languageToTitleMap[surveyState.insActiveLanguage] ?? surveyState.surveyQuestionnairesList[prevNavContext.surveyQuestionnaireIndex]?.pages[prevNavContext.surveyQuestionnairePageIndex].title ?? SurveyResponseProgressTextHelper.mapPageTypeToText[prevNavContext.mainNavIndex],
                    next: SurveyResponseProgressTextHelper.getNextTextByNavigationContext(inventory, prevNavContext, coreDataResult.data, surveyState.insActiveLanguage),
                    currentProgress: SurveyResponseProgressTextHelper.getCurrentInventoryStep(inventory, prevNavContext),
                    maxProgress: SurveyResponseProgressTextHelper.getMaxNumberOfInventorySteps(inventory)
                }
            });

            return;
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                responseId: responseId,
                dataLoaded: true,
                coreData: coreDataResult.data,
                validationResult: {
                    ...validationsResult.data
                },

                navContext: prevNavContext ?? getInitialNavContext()
            }
        });

        const pagesOrder = SurveyResponseProgressTextHelper.topLevelPagesOrder(coreDataResult.data);

        dispatch({
            type: SurveyResponseSetProgressState,
            value: {
                title: "Digital Product Passport",
                current: prevNavContext !== null ? SurveyResponseProgressTextHelper.mapPageTypeToText[prevNavContext.mainNavIndex] : SurveyResponseProgressTextHelper.Instructions,
                next: SurveyResponseProgressTextHelper.mapPageTypeToText[pagesOrder[pagesOrder.indexOf(prevNavContext !== null ? prevNavContext.mainNavIndex : SurveyPageType.Instructions) + 1]]
            }
        });
    },

    getCoreSurveyDataForAddedMaterials: (responseId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if ((surveyState === null || surveyState === undefined)) {
            return;
        }

        dispatch({ type: SurveyResponseSetState, value: { dataLoaded: false } });
        // Fetch data for the new page from the backend
        const coreDataResult = await serviceClient.getCoreSurveyResponseDataForAddedMaterials(responseId);
        if (!coreDataResult.success || !coreDataResult.data) {
            dispatch({
                type: SurveyResponseSetState,
                value: {
                    showErrorMessage: true,
                    dataLoaded: true
                }
            });

            return;
        }

        dispatch({ type: SurveyResponseSetState, value: { dataLoaded: true } });
        return coreDataResult.data;
    },

    updateSupplierQuestionnairePageVisited: (pageIndex: number, set: boolean = true): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (!surveyState) {
            return;
        }

        const questionniare: Questionnaire = { ...surveyState.coreData!.response.supplierQuestionnaireData?.questionnaire! };

        if (!questionniare) {
            return;
        }

        const page = questionniare.pages[pageIndex];

        if (!page) {
            return;
        }

        if (page.hasPageBeenVisited === set) {
            return;
        }

        page.hasPageBeenVisited = set;

        const apiResult = await serviceClient.questionnaireResponseUpdate(
            surveyState.responseId,
            "",
            false,
            questionniare,
            surveyState.coreData?.response.supplierQuestionnaireData?.files ?? []
        );

        if (!apiResult.success) {
            getState().appServices?.displayApiErrorMessage();
            return;
        }

        await updateSupplierQuestionnaireValidationResult(getState, dispatch);
    },

    updateSurveyResponseToSubmitted: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (!surveyState || !surveyState.coreData) {
            return;
        }

        // Clear the local storage item containing the last visited inventory ID
        // This prevents the last visited inventory ID from being included in future revision requests
        if (surveyState.coreData.response && surveyState.coreData.response.responseId) {
            localStorage.removeItem(`${LocalStorageNavContextKey}-${surveyState.coreData.response.responseId}`);
        }
        
        dispatch({
            type: SurveyResponseSetState,
            value: {
                coreData: {
                    ...surveyState.coreData,
                    response: {
                        ...surveyState.coreData.response,
                        state: 1 // submitted
                    }
                }
            }
        });
    },

    updateSelectSubInventoryCustomQuestionniarePageVisited: (questionnaireIndex: number, pageIndex: number, set: boolean = true): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (!surveyState) {
            return;
        }

        const questionniares: Questionnaire[] = [...surveyState.surveyQuestionnairesList];

        const questionnaire = questionniares[questionnaireIndex];

        if (!questionnaire) {
            return;
        }

        const page = questionnaire.pages[pageIndex];

        if (!page) {
            return;
        }

        page.hasPageBeenVisited = set;

        const type = surveyState.questionnairesTypes[questionnaireIndex];

        // Base Questionnaire does not actually get saved. It is generated as
        // part of the request/response.
        if (type === InventoryQuestionnaireType.BaseQuestionnaire) {
            return;
        }

        const apiResult = await serviceClient.questionnaireResponseUpdate(
            surveyState.responseId,
            surveyState.selectedInventory!.inventoryId,
            type === InventoryQuestionnaireType.AttestationQuestionnaire,
            questionnaire,
            surveyState.coreData?.response.supplierQuestionnaireData?.files ?? []
        );

        if (!apiResult.success) {
            getState().appServices?.displayApiErrorMessage();
            return;
        }

        await updateValidationResult(getState, dispatch, [surveyState.selectedInventory!.inventoryId]);
    },

    dispatchUpdateInventoryDetails: (result: any, inventoryId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const surveyState = getState().surveyResponseState;

        if (!surveyState) {
            return;
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                pageDataLoaded: true,
                selectedInventory: {
                    ...result.data,
                    bomNextClicked: surveyState.selectedInventory?.bomNextClicked,
                    inventoryId: inventoryId
                },
                surveyQuestionnairesList: result.data.questionnaireList,
                questionnairesTypes: result.data.questionnairesTypes
            }
        });
    },
    
    dispatchLoadingState: (isLoading: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: SurveyResponseSetState,
            value: {
                pageDataLoaded: !isLoading
            }
        });
    },

    initializeRegulationManager: (regulationId: string, questionsList: QuestionnaireQuestion[], questionRegulatoryData: QuestionRegulatoryData[], regulationScoresTable: RegulationScoresTable | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const surveyState = getState().surveyResponseState ?? getInitialState("en");

        const overallQuestionniare = surveyState.surveyQuestionnairesList[surveyState.navContext.surveyQuestionnaireIndex];

        // special case, SCIP uses the scores from REACH SVHC
        const regulationIdToUseForScoring = (regulationId === RegulationManager.ScipComplianceId)
            ? RegulationManager.ReachSvhcCandidateRegulationId
            : regulationId;

        const complianceScoreResult = regulationId && regulationId.length > 0 && regulationId.toLowerCase() != RegulationManager.ConflictMineralsV2SupplierQuestionnaireRegulationId.toLowerCase()
            ? regulationScoresTable![regulationIdToUseForScoring]
            : null;
    
        const regulationManager = CustomRegulationLogicManagerFactory.createManagerForRegulation(
            regulationId,
            surveyState.coreData?.response ?? null,
            questionsList,
            overallQuestionniare,
            questionRegulatoryData,
            surveyState.selectedInventory,
            complianceScoreResult
        );

        dispatch({
            type: SurveyResponseSetState,
            value: {
                regulationManager: regulationManager
            }
        });
    },

    fetchScipSubmissionForSelectedInventory: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        const currentResponseState = state.surveyResponseState;

        if (!currentResponseState) {
            return;
        }

        if (!currentResponseState.selectedInventory) {
            return;
        }

        const apiResult = await serviceClient.scipSubmissionGet(currentResponseState.selectedInventory.inventoryId, false, true);

        if (!apiResult.success || !apiResult.data) {
            state.appServices?.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: SurveyResponseSetState,
            value: {
                selectedInventory: {
                    ...currentResponseState.selectedInventory,
                    scipSubmissionData: apiResult.data.scipSubmissionData
                }
            }
        });
    }
};


// ===============================================================================

export const reducer: Reducer<SurveyResponseState> = (state: SurveyResponseState | undefined, action: Action): SurveyResponseState => {
    if (state === undefined) {
        return getInitialState("en");
    }

    switch (action.type) {
        case SurveyResponseSetProgressState:
            const surveyResponseProgressState = (action as SurveyResponseSetProgressState).value;

            return {
                ...state,
                progressState: {
                    ...state.progressState,
                    ...surveyResponseProgressState
                }
            };

        case SurveyResponseSetState:
            const surveyResponseState = (action as SurveyResponseSetState).value;

            // Only store the navigation context in local storage if the survey response is not in a submitted state or being submitted
            if (state.coreData !== null && state.coreData !== undefined) {
                // Check if the survey is in a submitted state (state = 1) or if the offline process has started
                const isSubmitted = state.coreData.response.state === 1 ||
                                   (surveyResponseState.coreData && surveyResponseState.coreData.response && surveyResponseState.coreData.response.state === 1);
                
                const isOfflineProcessStarted = state.coreData.response.responseDataJson &&
                                               JSON.parse(state.coreData.response.responseDataJson).IsOfflineProcessStarted;
                
                if (!isSubmitted && !isOfflineProcessStarted) {
                    // Only track the last visited material if the survey is not submitted or being submitted
                    localStorage.setItem(`${LocalStorageNavContextKey}-${state.coreData.response.responseId}`, JSON.stringify(surveyResponseState.navContext ?? state.navContext));
                }
            }

            return {
                ...state,
                ...surveyResponseState
            };
    }

    return state;
}
